import { IRevenue } from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import { getFieldByActualsValueType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getFieldByActualsValueType'
import { getPercentDiff } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPercentDiff'
import { getPostfixByActualsValueType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/getPostfixByActualsValueType'
import { getDataWithChangedTotal } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/revenue/helpers/getDataWithChangedTotal'

import { ActualsValueTypes } from 'partner360/models/actualValueTypes'

import { IChangeComputedFieldsProps } from '../../../models/IChangeComputedFieldsProps'

const getActualsValueTypeByField = (
  field: string,
  actualValueTypes: ActualsValueTypes[]
): ActualsValueTypes => {
  const matchedValueType = actualValueTypes.find((valueType) => {
    const postfix = getPostfixByActualsValueType(valueType)

    return postfix && field.endsWith(postfix)
  })

  return matchedValueType ?? ActualsValueTypes.Ndp
}

const getBaseFieldByActualsValueType = (field: string, valueType: ActualsValueTypes): string => {
  const postfix = getPostfixByActualsValueType(valueType)

  if (!postfix) {
    return field
  }

  return field.slice(0, -postfix.length)
}

const getNumberField = (item: IRevenue, field: keyof IRevenue): number => {
  return Number(item[field] ?? 0)
}

const recomputePrevYearDataByQuarters = (
  item: IRevenue,
  valueType: ActualsValueTypes
): Partial<IRevenue> => {
  const prevYearQuarter1Field = getFieldByActualsValueType<IRevenue>('PrevYearQuarter1', valueType)
  const prevYearQuarter2Field = getFieldByActualsValueType<IRevenue>('PrevYearQuarter2', valueType)
  const prevYearQuarter3Field = getFieldByActualsValueType<IRevenue>('PrevYearQuarter3', valueType)
  const prevYearQuarter4Field = getFieldByActualsValueType<IRevenue>('PrevYearQuarter4', valueType)
  const previousYearRevenueField = getFieldByActualsValueType<IRevenue>(
    'PreviousYearRevenue',
    valueType
  )

  const sum =
    getNumberField(item, prevYearQuarter1Field) +
    getNumberField(item, prevYearQuarter2Field) +
    getNumberField(item, prevYearQuarter3Field) +
    getNumberField(item, prevYearQuarter4Field)

  return {
    [previousYearRevenueField]: Number(sum.toFixed(2)),
  } as Partial<IRevenue>
}

const recomputeCurrentYearDataByQuarters = (
  item: IRevenue,
  valueType: ActualsValueTypes
): Partial<IRevenue> => {
  const currentYearQuarter1Field = getFieldByActualsValueType<IRevenue>(
    'CurrentYearQuarter1',
    valueType
  )
  const currentYearQuarter2Field = getFieldByActualsValueType<IRevenue>(
    'CurrentYearQuarter2',
    valueType
  )
  const currentYearQuarter3Field = getFieldByActualsValueType<IRevenue>(
    'CurrentYearQuarter3',
    valueType
  )
  const currentYearQuarter4Field = getFieldByActualsValueType<IRevenue>(
    'CurrentYearQuarter4',
    valueType
  )
  const currentYearRevenueField = getFieldByActualsValueType<IRevenue>(
    'CurrentYearRevenue',
    valueType
  )

  const sum =
    getNumberField(item, currentYearQuarter1Field) +
    getNumberField(item, currentYearQuarter2Field) +
    getNumberField(item, currentYearQuarter3Field) +
    getNumberField(item, currentYearQuarter4Field)

  return {
    [currentYearRevenueField]: Number(sum.toFixed(2)),
  } as Partial<IRevenue>
}

const reComputeYoY = (
  item: IRevenue,
  updatedComputedFields: Partial<IRevenue>,
  valueType: ActualsValueTypes
): Partial<IRevenue> => {
  const currentYearRevenueField = getFieldByActualsValueType<IRevenue>(
    'CurrentYearRevenue',
    valueType
  )
  const previousYearRevenueField = getFieldByActualsValueType<IRevenue>(
    'PreviousYearRevenue',
    valueType
  )
  const yoyField = getFieldByActualsValueType<IRevenue>('YoY', valueType)

  const currentYearRevenue = Number(
    updatedComputedFields[currentYearRevenueField] ?? item[currentYearRevenueField] ?? 0
  )

  const previousYearRevenue = Number(
    updatedComputedFields[previousYearRevenueField] ?? item[previousYearRevenueField] ?? 0
  )

  return {
    [yoyField]: getPercentDiff(currentYearRevenue, previousYearRevenue),
  } as Partial<IRevenue>
}

const getUpdatedComputedFields = (
  item: IRevenue,
  field: string,
  actualValueTypes: ActualsValueTypes[]
): Partial<IRevenue> => {
  const valueType = getActualsValueTypeByField(field, actualValueTypes)
  const baseField = getBaseFieldByActualsValueType(field, valueType)

  let updatedComputedFields: Partial<IRevenue> = {}

  if (baseField.startsWith('CurrentYearQuarter')) {
    updatedComputedFields = {
      ...updatedComputedFields,
      ...recomputeCurrentYearDataByQuarters(item, valueType),
    }
  }

  if (baseField.startsWith('PrevYearQuarter')) {
    updatedComputedFields = {
      ...updatedComputedFields,
      ...recomputePrevYearDataByQuarters(item, valueType),
    }
  }

  if (
    baseField.startsWith('CurrentYearQuarter') ||
    baseField.startsWith('PrevYearQuarter') ||
    baseField === 'CurrentYearRevenue' ||
    baseField === 'PreviousYearRevenue'
  ) {
    updatedComputedFields = {
      ...updatedComputedFields,
      ...reComputeYoY(item, updatedComputedFields, valueType),
    }
  }

  return updatedComputedFields
}

export const changeRevenueComputedFields = ({
  field,
  keyField,
  keyValue,
  data,
  actualValueTypes = [],
}: IChangeComputedFieldsProps<IRevenue>): IRevenue[] => {
  const itemFoundIndex = data.findIndex((el) => el[keyField] === keyValue)
  const item = data[itemFoundIndex]

  if (item) {
    const updatedComputedFields = getUpdatedComputedFields(item, field, actualValueTypes)

    data[itemFoundIndex] = {
      ...data[itemFoundIndex],
      ...updatedComputedFields,
    }
  }

  return getDataWithChangedTotal(data, actualValueTypes)
}
